import type { Timestamp } from "firebase/firestore";
import type { EndUser, EndUserStatus } from "@/types/firestore";
import { str, strOrNull, numOrNull, arr, tsOrNull } from "@/lib/normalizeFields";

/**
 * Fills in every field `EndUser` requires with a safe default. endUsers has
 * six create paths (contact shadow, workshop contact, Pickaxe webhook,
 * scheduled sync, invite, invite-link redeem) and the invite/redeem pair
 * historically omitted `pickaxeUserId`, `pickaxeTotalUses`,
 * `pickaxeTotalMemories`, `pickaxeSignupDate`, and `lastActiveAt` — so
 * production docs exist that carry `undefined` where the type promises
 * `X | null`. Same failure class as the contacts crash normalizeContact
 * defends against. Client reads reach this through `converters.endUsers`
 * (src/lib/firestore.ts), whose fromFirestore delegates here.
 *
 * Deliberately lossy: fields NOT declared on `EndUser` (e.g. the server-only
 * `pickaxeProductIds`) are dropped, unlike the bare cast this replaced —
 * never spread a normalized user back into a Firestore write.
 */
export function normalizeEndUser(data: Record<string, unknown>): EndUser {
  const out: EndUser = {
    studioId: str(data.studioId),
    orgId: str(data.orgId),
    email: str(data.email),
    name: str(data.name),
    groupIds: arr(data.groupIds),

    pickaxeAccessGroupId: strOrNull(data.pickaxeAccessGroupId),
    pickaxeUserId: strOrNull(data.pickaxeUserId),
    pickaxeTotalUses: numOrNull(data.pickaxeTotalUses),
    pickaxeTotalMemories: numOrNull(data.pickaxeTotalMemories),
    pickaxeSignupDate: tsOrNull(data.pickaxeSignupDate),
    notes: strOrNull(data.notes),

    // "invited" only when the field is genuinely absent or empty — it is the
    // least-privileged state, and a real "archived"/"suspended"/"contact"
    // must never be overridden by a defaulting pass. Unknown non-empty
    // strings pass through so a status value added server-side first is
    // preserved rather than rewritten by a stale client.
    status: (typeof data.status === "string" && data.status !== ""
      ? data.status
      : "invited") as EndUserStatus,

    // The type declares these two non-nullable, but a malformed doc (e.g. a
    // merge-set ghost) can lack them. Every current reader null-guards via a
    // formatDate-style helper, and both list hooks orderBy("createdAt"),
    // which already excludes createdAt-less docs server-side — the cast
    // keeps the type ergonomic for the every-writer-stamps-it common case.
    invitedAt: tsOrNull(data.invitedAt) as Timestamp,
    lastReinvitedAt: tsOrNull(data.lastReinvitedAt),
    verifiedAt: tsOrNull(data.verifiedAt),
    lastActiveAt: tsOrNull(data.lastActiveAt),
    createdAt: tsOrNull(data.createdAt) as Timestamp,
    createdBy: str(data.createdBy),
  };

  // Optional fields stay absent when absent — a missing `userKind` MEANS
  // "legacy studio_user" (see the type), so stamping a default would change
  // the doc's meaning, not repair it.
  if (typeof data.userKind === "string") out.userKind = data.userKind as EndUser["userKind"];
  if (Array.isArray(data.tagIds)) out.tagIds = data.tagIds as string[];
  if (data.integrations !== undefined) out.integrations = data.integrations as EndUser["integrations"];
  if (data.tosAcceptance !== undefined) out.tosAcceptance = data.tosAcceptance as EndUser["tosAcceptance"];

  return out;
}
